import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Target, GraduationCap, Lightbulb, Globe, BookOpen, Languages, CalendarClock, Wallet, Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProgressBar } from "@/components/ProgressRing";
import { EligibilityBadge } from "@/components/common";
import { useApp } from "@/context/AppContext";
import { computeReadiness, computeMatch } from "@/lib/calc";
import { scholarships } from "@/data/scholarships";

const choice = (opts) => opts.map((o) => (typeof o === "string" ? { value: o, label: o } : o));

const steps = [
  { key: "careerGoal", icon: Target, q: "Apa tujuan studimu?", type: "choice", options: choice(["AI / Technology", "Business / Management", "Public Policy", "Science / Research", "Health", "Engineering", "Lainnya"]) },
  { key: "degreeTarget", icon: GraduationCap, q: "Degree apa yang ingin kamu ambil?", type: "choice", options: choice(["Master", "PhD / Doctoral", "Bachelor"]) },
  { key: "field", icon: Lightbulb, q: "Bidang apa yang kamu minati?", type: "choice", options: choice(["Computer Science", "Data Science", "Engineering", "Business", "Public Policy", "Science", "Health", "Lainnya"]) },
  { key: "countries", icon: Globe, q: "Negara mana yang kamu pertimbangkan?", type: "choice", options: choice(["United Kingdom", "Australia", "Europe", "Jepang", "Amerika Serikat", "UK / Australia / Europe"]) },
  { key: "education", icon: BookOpen, q: "Bagaimana profil akademikmu?", type: "compound-academic" },
  { key: "languageScore", icon: Languages, q: "Bagaimana kemampuan bahasamu?", type: "compound-language" },
  { key: "targetIntake", icon: CalendarClock, q: "Kapan target intake-mu?", type: "choice", options: choice(["2026", "2027", "2028", "Belum yakin"]) },
  { key: "budget", icon: Wallet, q: "Bagaimana kondisi budget-mu?", type: "choice", options: choice(["Limited", "Standard", "Higher Comfort"]) },
];

export default function Onboarding() {
  const { updateData, data, displayName } = useApp();
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({ ...data.profile });
  const [done, setDone] = useState(false);

  const step = steps[i];
  const set = (patch) => setAnswers((a) => ({ ...a, ...patch }));
  const canNext = () => {
    if (step.type === "compound-academic") return !!answers.education && !!answers.gpa;
    if (step.type === "compound-language") return !!answers.languageScore;
    return !!answers[step.key];
  };

  const next = () => {
    if (i < steps.length - 1) setI(i + 1);
    else finish();
  };
  const finish = () => {
    updateData((prev) => ({ ...prev, profile: { ...prev.profile, ...answers }, onboardingComplete: true }));
    setDone(true);
  };

  if (done) return <Snapshot profile={answers} onGo={() => navigate("/app")} />;

  const StepIcon = step.icon;
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FBFAFF] to-[#F3E8FF]">
      <div className="mx-auto flex min-h-screen max-w-xl flex-col px-4 py-8">
        <Logo />
        <div className="mt-8">
          <div className="flex items-center justify-between text-xs font-medium text-slate-500">
            <span>Langkah {i + 1} dari {steps.length}</span>
            <span>{Math.round(((i + 1) / steps.length) * 100)}%</span>
          </div>
          <ProgressBar value={((i + 1) / steps.length) * 100} className="mt-2" />
        </div>

        <div className="mt-8 flex flex-1 flex-col rounded-3xl border border-purple-100 bg-white p-6 shadow-sm sm:p-8" data-testid={`onboarding-step-${i}`}>
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F3E8FF] to-[#EDE9FE] text-[#7C3AED]"><StepIcon className="h-6 w-6" /></span>
          <h2 className="mt-5 font-display text-2xl font-bold text-slate-900">{step.q}</h2>

          <div className="mt-6 flex-1">
            {step.type === "choice" && (
              <div className="grid gap-2.5 sm:grid-cols-2">
                {step.options.map((o) => (
                  <button key={o.value} onClick={() => set({ [step.key]: o.value })}
                    data-testid={`onboarding-opt-${o.value}`}
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-medium transition-all ${
                      answers[step.key] === o.value ? "border-[#7C3AED] bg-purple-50 text-[#4C1D95]" : "border-purple-100 text-slate-600 hover:border-purple-200 hover:bg-purple-50/50"
                    }`}>
                    {o.label}
                    {answers[step.key] === o.value && <Check className="h-4 w-4 text-[#7C3AED]" />}
                  </button>
                ))}
              </div>
            )}
            {step.type === "compound-academic" && (
              <div className="space-y-4">
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {choice(["Bachelor Student", "Fresh Graduate", "Professional", "Master Student"]).map((o) => (
                    <button key={o.value} onClick={() => set({ education: o.value })}
                      className={`rounded-xl border px-4 py-3 text-sm font-medium ${answers.education === o.value ? "border-[#7C3AED] bg-purple-50 text-[#4C1D95]" : "border-purple-100 text-slate-600 hover:bg-purple-50/50"}`}>{o.label}</button>
                  ))}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">GPA / IPK (skala 4.0)</label>
                  <Input type="number" step="0.01" min="0" max="4" data-testid="onboarding-gpa" placeholder="cth. 3.62" value={answers.gpa || ""}
                    onChange={(e) => set({ gpa: e.target.value })} className="mt-1.5" />
                </div>
              </div>
            )}
            {step.type === "compound-language" && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  {["IELTS", "TOEFL"].map((t) => (
                    <button key={t} onClick={() => set({ language: t })}
                      className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-medium ${answers.language === t ? "border-[#7C3AED] bg-purple-50 text-[#4C1D95]" : "border-purple-100 text-slate-600"}`}>{t}</button>
                  ))}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Skor {answers.language || "IELTS"} (kosongkan bila belum ada)</label>
                  <Input type="number" step="0.5" data-testid="onboarding-language" placeholder="cth. 6.5" value={answers.languageScore || ""}
                    onChange={(e) => set({ languageScore: e.target.value })} className="mt-1.5" />
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" onClick={() => (i === 0 ? navigate("/app") : setI(i - 1))} className="text-slate-500">
              <ArrowLeft className="mr-1 h-4 w-4" /> {i === 0 ? "Lewati" : "Kembali"}
            </Button>
            <Button onClick={next} disabled={!canNext()} data-testid="onboarding-next" className="rounded-full bg-[#7C3AED] px-6 hover:bg-[#6D28D9]">
              {i === steps.length - 1 ? "Selesai" : "Lanjut"} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Snapshot({ profile, onGo }) {
  const readiness = computeReadiness(profile, {});
  const ranked = scholarships.map((s) => ({ s, m: computeMatch(profile, s) })).sort((a, b) => b.m.matchScore - a.m.matchScore).slice(0, 3);
  const gaps = [...readiness.domains].sort((a, b) => a.score - b.score).slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FBFAFF] to-[#F3E8FF]">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-[#6D28D9]"><Sparkles className="h-3.5 w-3.5" /> Your Starting Snapshot</span>
          <h1 className="mt-4 font-display text-3xl font-extrabold text-slate-900">Halo, {profile.name || "👋"}, ini titik awalmu</h1>
          <p className="mt-2 text-slate-600">Readiness awal <b className="text-[#5B21B6]">{readiness.overall}%</b> ({readiness.method.version}). Ini diagnostik persiapan, bukan probabilitas diterima.</p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-purple-100 bg-white p-5">
            <h3 className="font-display text-sm font-bold text-slate-800">3 Peluang Relevan</h3>
            <ul className="mt-3 space-y-2">
              {ranked.map(({ s, m }) => (
                <li key={s.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{s.name}</span>
                  <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-bold text-[#7C3AED]">{m.matchScore}%</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-purple-100 bg-white p-5">
            <h3 className="font-display text-sm font-bold text-slate-800">3 Gap Persiapan</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {gaps.map((g) => <li key={g.key} className="flex justify-between"><span>{g.label}</span><span className="font-semibold text-slate-400">{g.score}%</span></li>)}
            </ul>
          </div>
          <div className="rounded-2xl border border-purple-100 bg-white p-5">
            <h3 className="font-display text-sm font-bold text-slate-800">3 Langkah Berikutnya</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {gaps.map((g, idx) => <li key={g.key} className="flex gap-2"><span className="text-[#7C3AED]">{idx + 1}.</span> {g.action}</li>)}
            </ul>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Button size="lg" onClick={onGo} data-testid="snapshot-continue" className="rounded-full bg-[#7C3AED] px-8 hover:bg-[#6D28D9]">Buka Dashboard <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
